import type {SpanAttribute, SpanEntry} from './copilot-chat-types'

export interface SpanNode {
  name: string
  spanId: string
  attributes: SpanAttribute[] | null
  startTime: number
  endTime: number
  children: SpanNode[]
}

/**
 * Converts a JSON encoded SpanEntry array into a tree of SpanNodes.
 */
export function processTraceData(entries: SpanEntry[]): SpanNode[] {
  const spanNodeMap = new Map<string, SpanNode>()
  const roots: SpanNode[] = []

  const spanEntryToSpanNode = (entry: SpanEntry): SpanNode => {
    const node: SpanNode = {
      name: entry.name,
      spanId: entry.spanId,
      attributes: entry.attributes,
      startTime: entry.startTime,
      endTime: entry.endTime,
      children: [],
    }

    spanNodeMap.set(node.spanId, node)

    const parentNode = entry.parentId && spanNodeMap.get(entry.parentId)
    if (parentNode) {
      parentNode.children.push(node)
    } else {
      roots.push(node)
    }
    return node
  }

  // rely on side effects to set roots
  entries.map(spanEntryToSpanNode)

  // return nodes without parent references so we don't have circular references
  return roots.map(orphanize)
}

/**
 * Strips the parent references from a SpanNodeWithParent to create a SpanNode.
 * This is necessary to avoid circular references in the tree structure and make it possible to JSON encode.
 */
function orphanize(root: SpanNode): SpanNode {
  return {
    name: root.name,
    spanId: root.spanId,
    attributes: root.attributes,
    startTime: root.startTime,
    endTime: root.endTime,
    children: root.children.map(orphanize),
  }
}
